import React, { useEffect, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import CustomModalContainer from "../../../components/modals/CustomModalContainer";
import Button from "../../ui/Button";
import OTPInput from "../../ui/OTPInput";
import axiosInstance from "../../../apis/baseUrl";

interface SetTwoFactorAuthProps {
  open: boolean;
  onClose?: () => void;
  onSuccess?: () => void;
}

const SetTwoFactorAuth: React.FC<SetTwoFactorAuthProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [qrCodeValue, setQrCodeValue] = useState("");

  const handleOTPComplete = async (enteredOtp: string) => {
    try {
      const response = await axiosInstance.post("v1/auth/verify-2fa", {
        otp: enteredOtp,
      });

      if (response.data.success) {
        setError(null);
        console.log("OTP verified successfully!");
        onSuccess && onSuccess();
      } else {
        setError("Invalid OTP. Please try again.");
      }
    } catch (error) {
      setError("An error occurred while verifying OTP. Please try again.");
      console.error("Error verifying OTP:", error);
    }
  };

  const generate2FAURL = async () => {
    const res = await axiosInstance.get("v1/auth/generate-2fa-url");
    const url = res?.data?.data;
    setQrCodeValue(url);

    // Extract the secret from the URL
    const urlObj = new URL(url);
    const secretParam = new URLSearchParams(urlObj.search).get("secret");
    if (secretParam) {
      setSecret(secretParam);
    }
  };

  useEffect(() => {
    generate2FAURL();
  }, []);

  if (!open) return null;

  return (
    <CustomModalContainer
      title="Set up Two-factor Authentication"
      footer={
        <div className="flex justify-between items-center p-4">
          <Button
            type="button"
            onClick={onClose}
            label="Cancel"
            customStyle={{
              width: "auto",
              minWidth: "150px",
              background: "none",
              color: "#000",
            }}
          />
          <Button
            label="Confirm code"
            type="submit"
            customStyle={{
              width: "auto",
              minWidth: "150px",
            }}
            onClick={onSuccess}
          />
        </div>
      }
      open={open}
      onClose={onClose}
    >
      <div className="p-6">
        <p className="text-gray-600 mb-4">
          Using an authenticator app like Microsoft Authenticator, Google
          Authenticator, Authy, or 1Password, scan the QR code. It will generate
          a 6-digit code for you to enter below.
        </p>
        <div className="flex flex-col items-center space-y-4">
          {/* QR Code */}
          <QRCodeCanvas value={qrCodeValue} size={150} />

          <div className="w-full">
            <p className="text-gray-600 text-sm mb-2">
              Or copy the secret key into your Authenticator app:
            </p>
            <div className="flex w-full gap-2">
              <div className="flex items-center justify-between bg-yellow-50 border border-gray-300 rounded-md  py-2 overflow-hidden">
                <span className="text-gray-800 text-sm px-4">{secret}</span>
              </div>
              <button
                className="text-accent hover:underline text-sm"
                onClick={() => navigator.clipboard.writeText(secret)}
              >
                Copy
              </button>
            </div>
          </div>
        </div>

        {/* OTP Input */}
        <form className="space-y-4 mt-6">
          <p className="text-md font-semibold text-gray-600">
            Enter the 6-digit authentication code generated by your app:
          </p>
          <OTPInput length={6} onComplete={handleOTPComplete} />
          {error && <p className="text-red-500 text-sm mt-2">{error}</p>}

          <p className="text-sm text-gray-500 mt-6 text-center">
            Didn't receive a code?{" "}
            <button
              type="button"
              className="text-accent hover:underline"
              onClick={() => console.log("Resend OTP clicked")}
            >
              Resend
            </button>
          </p>
        </form>
      </div>
    </CustomModalContainer>
  );
};

export default SetTwoFactorAuth;
